/**
 * Stock Design Library for NFC Card Designer
 *
 * Curated professional designs organized by use case.
 * All designs work instantly without AI generation.
 */

// ========== AI STYLE VARIATIONS ==========
export const STYLE_VARIATIONS = [
  {
    id: 'style-cyberpunk',
    name: 'Cyberpunk',
    description: 'Neon-lit, futuristic digital aesthetic',
    promptSuffix: ', cyberpunk aesthetic, neon lights, dark background, glowing edges, digital grid, holographic elements, high contrast',
    color: '#00ffff'
  },
  {
    id: 'style-holographic',
    name: 'Holographic',
    description: 'Iridescent rainbow shimmer effect',
    promptSuffix: ', holographic iridescent surface, rainbow gradient, pearlescent shine, chrome reflection, prismatic colors, metallic sheen',
    color: '#ff00ff'
  },
  {
    id: 'style-minimal',
    name: 'Minimal',
    description: 'Clean, simple, high-contrast design',
    promptSuffix: ', minimalist design, clean lines, simple shapes, high contrast, monochromatic, geometric, modern, professional',
    color: '#ffffff'
  },
  {
    id: 'style-glitch',
    name: 'Glitch',
    description: 'Digital corruption, RGB split effect',
    promptSuffix: ', glitch art, digital distortion, RGB chromatic aberration, corrupted data, scan lines, VHS aesthetic, cybernetic',
    color: '#ff0055'
  },
  {
    id: 'style-gradient-mesh',
    name: 'Gradient Mesh',
    description: 'Smooth flowing color gradients',
    promptSuffix: ', smooth gradient mesh, flowing colors, soft blend, pastel gradients, dreamy atmosphere, subtle transitions',
    color: '#ffd700'
  },
  {
    id: 'style-geometric',
    name: 'Geometric',
    description: 'Angular shapes and patterns',
    promptSuffix: ', geometric pattern, angular shapes, triangles, polygons, abstract composition, sharp edges, modern design',
    color: '#00ff88'
  },
  {
    id: 'style-vaporwave',
    name: 'Vaporwave',
    description: 'Retro 80s/90s aesthetic',
    promptSuffix: ', vaporwave aesthetic, retro 80s 90s, pink and cyan colors, palm trees, grids, nostalgia, sunset gradient',
    color: '#ff6ec7'
  },
  {
    id: 'style-metallic',
    name: 'Metallic',
    description: 'Brushed metal, industrial look',
    promptSuffix: ', brushed metal texture, metallic surface, industrial design, steel, aluminum, chrome finish, reflective',
    color: '#c0c0c0'
  }
];

// Helper to generate AI prompt for style variation
export function generateStylePrompt(baseDesign, styleVariation) {
  let basePrompt = '';

  if (baseDesign.prompt) {
    basePrompt = baseDesign.prompt;
  } else if (baseDesign.category === 'gradient') {
    basePrompt = `abstract gradient background with ${baseDesign.colors.join(', ')} colors`;
  } else if (baseDesign.category === 'pattern') {
    basePrompt = `${baseDesign.name.toLowerCase()} pattern design`;
  } else {
    basePrompt = 'abstract card design';
  }

  return basePrompt + styleVariation.promptSuffix;
}

export const STOCK_DESIGNS = {
  // ========== PROFESSIONAL / BUSINESS ==========
  gradients: [
    // Black & Dark Professional
    {
      id: 'pro-midnight',
      name: 'Midnight Black',
      category: 'gradient',
      type: 'linear',
      colors: ['#0a0a0b', '#18181b', '#27272a'],
      angle: 135,
      preview: 'linear-gradient(135deg, #0a0a0b, #18181b, #27272a)',
      tags: ['professional', 'business', 'dark']
    },
    {
      id: 'pro-charcoal',
      name: 'Charcoal Steel',
      category: 'gradient',
      type: 'linear',
      colors: ['#1e1e1e', '#2d2d2d', '#3c3c3c'],
      angle: 90,
      preview: 'linear-gradient(90deg, #1e1e1e, #2d2d2d, #3c3c3c)',
      tags: ['professional', 'minimal', 'dark']
    },
    {
      id: 'pro-slate',
      name: 'Slate Professional',
      category: 'gradient',
      type: 'linear',
      colors: ['#1e293b', '#334155', '#475569'],
      angle: 180,
      preview: 'linear-gradient(180deg, #1e293b, #334155, #475569)',
      tags: ['professional', 'corporate']
    },

    // Blue Professional
    {
      id: 'pro-navy',
      name: 'Navy Blue',
      category: 'gradient',
      type: 'linear',
      colors: ['#0f172a', '#1e3a8a', '#2563eb'],
      angle: 135,
      preview: 'linear-gradient(135deg, #0f172a, #1e3a8a, #2563eb)',
      tags: ['professional', 'corporate', 'blue']
    },
    {
      id: 'pro-ocean',
      name: 'Ocean Professional',
      category: 'gradient',
      type: 'linear',
      colors: ['#0c4a6e', '#0369a1', '#0284c7'],
      angle: 90,
      preview: 'linear-gradient(90deg, #0c4a6e, #0369a1, #0284c7)',
      tags: ['professional', 'blue', 'corporate']
    },

    // Tech / Modern
    {
      id: 'tech-cyber',
      name: 'Cyber Green',
      category: 'gradient',
      type: 'linear',
      colors: ['#000000', '#064e3b', '#00ff88'],
      angle: 135,
      preview: 'linear-gradient(135deg, #000000, #064e3b, #00ff88)',
      tags: ['tech', 'modern', 'green']
    },
    {
      id: 'tech-neon-blue',
      name: 'Neon Electric',
      category: 'gradient',
      type: 'linear',
      colors: ['#000000', '#0c4a6e', '#00d4ff'],
      angle: 90,
      preview: 'linear-gradient(90deg, #000000, #0c4a6e, #00d4ff)',
      tags: ['tech', 'modern', 'neon']
    },
    {
      id: 'tech-matrix',
      name: 'Digital Matrix',
      category: 'gradient',
      type: 'radial',
      colors: ['#001a00', '#003300', '#00ff00'],
      preview: 'radial-gradient(circle, #001a00, #003300, #00ff00)',
      tags: ['tech', 'matrix', 'green']
    },
    {
      id: 'tech-purple',
      name: 'Tech Purple',
      category: 'gradient',
      type: 'linear',
      colors: ['#1e1b4b', '#4c1d95', '#7c3aed'],
      angle: 135,
      preview: 'linear-gradient(135deg, #1e1b4b, #4c1d95, #7c3aed)',
      tags: ['tech', 'modern', 'purple']
    },

    // Vibrant / Creative
    {
      id: 'vibrant-sunset',
      name: 'Sunset Glow',
      category: 'gradient',
      type: 'linear',
      colors: ['#ff6b6b', '#feca57', '#48dbfb'],
      angle: 135,
      preview: 'linear-gradient(135deg, #ff6b6b, #feca57, #48dbfb)',
      tags: ['vibrant', 'creative', 'warm']
    },
    {
      id: 'vibrant-fire',
      name: 'Fire Ember',
      category: 'gradient',
      type: 'linear',
      colors: ['#eb4511', '#f97316', '#fbbf24'],
      angle: 45,
      preview: 'linear-gradient(45deg, #eb4511, #f97316, #fbbf24)',
      tags: ['vibrant', 'warm', 'fire']
    },
    {
      id: 'vibrant-ocean',
      name: 'Ocean Deep',
      category: 'gradient',
      type: 'linear',
      colors: ['#1e3a8a', '#0ea5e9', '#06b6d4'],
      angle: 90,
      preview: 'linear-gradient(90deg, #1e3a8a, #0ea5e9, #06b6d4)',
      tags: ['vibrant', 'cool', 'ocean']
    },
    {
      id: 'vibrant-tropical',
      name: 'Tropical',
      category: 'gradient',
      type: 'linear',
      colors: ['#ec4899', '#f59e0b', '#10b981'],
      angle: 120,
      preview: 'linear-gradient(120deg, #ec4899, #f59e0b, #10b981)',
      tags: ['vibrant', 'tropical', 'colorful']
    },

    // Purple / Pink
    {
      id: 'purple-haze',
      name: 'Purple Haze',
      category: 'gradient',
      type: 'radial',
      colors: ['#7c3aed', '#a855f7', '#d946ef'],
      preview: 'radial-gradient(circle, #7c3aed, #a855f7, #d946ef)',
      tags: ['purple', 'creative']
    },
    {
      id: 'purple-pink',
      name: 'Purple Pink',
      category: 'gradient',
      type: 'linear',
      colors: ['#581c87', '#a21caf', '#ec4899'],
      angle: 135,
      preview: 'linear-gradient(135deg, #581c87, #a21caf, #ec4899)',
      tags: ['purple', 'pink', 'vibrant']
    },

    // Luxury / Premium
    {
      id: 'luxury-gold',
      name: 'Gold Luxury',
      category: 'gradient',
      type: 'linear',
      colors: ['#78350f', '#ca8a04', '#fbbf24'],
      angle: 135,
      preview: 'linear-gradient(135deg, #78350f, #ca8a04, #fbbf24)',
      tags: ['luxury', 'gold', 'premium']
    },
    {
      id: 'luxury-rose',
      name: 'Rose Gold',
      category: 'gradient',
      type: 'linear',
      colors: ['#881337', '#be123c', '#fb7185'],
      angle: 90,
      preview: 'linear-gradient(90deg, #881337, #be123c, #fb7185)',
      tags: ['luxury', 'rose', 'premium']
    },

    // Minimal / Clean
    {
      id: 'minimal-white',
      name: 'Pure White',
      category: 'gradient',
      type: 'linear',
      colors: ['#f8f9fa', '#e9ecef', '#dee2e6'],
      angle: 135,
      preview: 'linear-gradient(135deg, #f8f9fa, #e9ecef, #dee2e6)',
      tags: ['minimal', 'light', 'clean']
    },
    {
      id: 'minimal-gray',
      name: 'Minimal Gray',
      category: 'gradient',
      type: 'linear',
      colors: ['#f1f5f9', '#cbd5e1', '#94a3b8'],
      angle: 90,
      preview: 'linear-gradient(90deg, #f1f5f9, #cbd5e1, #94a3b8)',
      tags: ['minimal', 'gray', 'clean']
    },

    // Nature / Organic
    {
      id: 'nature-forest',
      name: 'Forest Green',
      category: 'gradient',
      type: 'linear',
      colors: ['#14532d', '#15803d', '#22c55e'],
      angle: 135,
      preview: 'linear-gradient(135deg, #14532d, #15803d, #22c55e)',
      tags: ['nature', 'green', 'organic']
    },
    {
      id: 'nature-earth',
      name: 'Earth Tones',
      category: 'gradient',
      type: 'linear',
      colors: ['#713f12', '#a16207', '#ca8a04'],
      angle: 90,
      preview: 'linear-gradient(90deg, #713f12, #a16207, #ca8a04)',
      tags: ['nature', 'earth', 'organic']
    },

    // Vaporwave / Retro
    {
      id: 'retro-vaporwave',
      name: 'Vaporwave',
      category: 'gradient',
      type: 'linear',
      colors: ['#ff6ec7', '#ff00ff', '#00ffff'],
      angle: 135,
      preview: 'linear-gradient(135deg, #ff6ec7, #ff00ff, #00ffff)',
      tags: ['retro', 'vaporwave', '80s']
    },
    {
      id: 'retro-sunset',
      name: 'Retro Sunset',
      category: 'gradient',
      type: 'linear',
      colors: ['#ff0080', '#ff8c00', '#40e0d0'],
      angle: 180,
      preview: 'linear-gradient(180deg, #ff0080, #ff8c00, #40e0d0)',
      tags: ['retro', 'sunset', 'vibrant']
    }
  ],

  // ========== PATTERN-BASED DESIGNS ==========
  patterns: [
    {
      id: 'pattern-grid',
      name: 'Grid Lines',
      category: 'pattern',
      patternType: 'grid',
      background: '#0a0a0b',
      foreground: '#00ff88',
      tags: ['tech', 'geometric', 'minimal']
    },
    {
      id: 'pattern-dots',
      name: 'Dot Matrix',
      category: 'pattern',
      patternType: 'dots',
      background: '#18181b',
      foreground: '#00d4ff',
      tags: ['tech', 'modern', 'minimal']
    },
    {
      id: 'pattern-waves',
      name: 'Wave Lines',
      category: 'pattern',
      patternType: 'waves',
      background: '#1e293b',
      foreground: '#3b82f6',
      tags: ['modern', 'fluid', 'creative']
    },
    {
      id: 'pattern-circuit',
      name: 'Circuit Board',
      category: 'pattern',
      patternType: 'circuit',
      background: '#000000',
      foreground: '#00ff00',
      tags: ['tech', 'cyberpunk', 'modern']
    },
    {
      id: 'pattern-hexagons',
      name: 'Hexagon Grid',
      category: 'pattern',
      patternType: 'hexagons',
      background: '#1e1e1e',
      foreground: '#7c3aed',
      tags: ['geometric', 'tech', 'modern']
    },
    {
      id: 'pattern-diagonal',
      name: 'Diagonal Stripes',
      category: 'pattern',
      patternType: 'diagonal',
      background: '#0f172a',
      foreground: '#2563eb',
      tags: ['minimal', 'geometric', 'professional']
    }
  ]
};

/**
 * Preset AI prompts for quick generation
 */
export const PRESET_PROMPTS = [
  {
    id: 'preset-cyberpunk',
    name: 'Cyberpunk Neon',
    category: 'Tech',
    prompt: 'cyberpunk neon pattern, holographic circuit board, futuristic tech aesthetic, vibrant colors, high contrast, 4k quality',
    tags: ['tech', 'neon', 'futuristic']
  },
  {
    id: 'preset-minimal',
    name: 'Minimal Geometric',
    category: 'Minimal',
    prompt: 'minimal geometric pattern, clean lines, modern design, monochrome, simple shapes, professional, 4k quality',
    tags: ['minimal', 'geometric', 'professional']
  },
  {
    id: 'preset-nature',
    name: 'Natural Organic',
    category: 'Nature',
    prompt: 'organic flowing natural pattern, botanical elements, earth tones, watercolor style, peaceful, 4k quality',
    tags: ['nature', 'organic', 'watercolor']
  },
  {
    id: 'preset-luxury',
    name: 'Luxury Gold',
    category: 'Luxury',
    prompt: 'luxury elegant pattern, gold accents, art deco style, sophisticated design, black and gold, premium, 4k quality',
    tags: ['luxury', 'elegant', 'gold']
  },
  {
    id: 'preset-space',
    name: 'Cosmic Space',
    category: 'Space',
    prompt: 'cosmic nebula pattern, deep space, stars and galaxies, purple and blue hues, celestial, 4k quality',
    tags: ['space', 'cosmic', 'celestial']
  },
  {
    id: 'preset-retro',
    name: 'Retro Wave',
    category: 'Retro',
    prompt: 'retro vaporwave pattern, 80s aesthetic, neon grid, sunset colors, nostalgic vibes, 4k quality',
    tags: ['retro', 'vaporwave', '80s']
  },
  {
    id: 'preset-abstract',
    name: 'Abstract Art',
    category: 'Abstract',
    prompt: 'abstract artistic pattern, vibrant colors, modern art style, dynamic composition, creative, 4k quality',
    tags: ['abstract', 'artistic', 'vibrant']
  },
  {
    id: 'preset-business',
    name: 'Professional Business',
    category: 'Business',
    prompt: 'professional business pattern, corporate design, clean and modern, navy blue and white, trustworthy, 4k quality',
    tags: ['business', 'professional', 'corporate']
  }
];

/**
 * Get all stock designs by category
 */
export function getStockDesignsByCategory(category) {
  return STOCK_DESIGNS[category] || [];
}

/**
 * Get all stock designs (flattened)
 */
export function getAllStockDesigns() {
  return [
    ...STOCK_DESIGNS.gradients,
    ...(STOCK_DESIGNS.patterns || [])
  ];
}

/**
 * Get design by ID
 */
export function getDesignById(id) {
  const allDesigns = getAllStockDesigns();
  return allDesigns.find(design => design.id === id);
}

/**
 * Get preset prompt by ID
 */
export function getPresetPromptById(id) {
  return PRESET_PROMPTS.find(preset => preset.id === id);
}

/**
 * Search designs by tag or name
 */
export function searchDesigns(query) {
  const allDesigns = getAllStockDesigns();
  const lowerQuery = query.toLowerCase();

  return allDesigns.filter(design => {
    const nameMatch = design.name.toLowerCase().includes(lowerQuery);
    const tagMatch = design.tags?.some(tag => tag.toLowerCase().includes(lowerQuery));
    const categoryMatch = design.category.toLowerCase().includes(lowerQuery);

    return nameMatch || tagMatch || categoryMatch;
  });
}
